import { qk, useSettings } from '@restaurant/api-client/react';
import { WEEKDAYS, type Settings } from '@restaurant/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Input, Textarea } from '@/components/ui/input';
import { PageHeader, Skeleton, Spinner, Switch } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { WhatsAppNumbersCard } from './WhatsAppNumbersCard';

const DAY_LABEL: Record<string, string> = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
const MSG_LABEL: Record<keyof Settings['statusMessages'], string> = {
  confirmed: 'Order confirmed',
  preparing: 'Preparing',
  ready: 'Ready for pickup',
  out_for_delivery: 'Out for delivery',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export function SettingsPage() {
  const { data } = useSettings();
  const qc = useQueryClient();
  const [s, setS] = useState<Settings>();
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  useEffect(() => data && setS(data), [data]);
  if (!s) return <div className="p-6"><Skeleton className="h-64" /></div>;

  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setS({ ...s, [k]: v });
  const rupees = (c: number) => String(c / 100);
  const cents = (v: string) => Math.round(Number(v.replace(/[^\d.]/g, '') || 0) * 100);

  const save = async () => {
    setBusy(true);
    setErrors({});
    try {
      const saved = await api.settings.update(s);
      qc.setQueryData(qk.settings, saved);
      toast.success('Settings saved');
    } catch (err) {
      const fe = (err as { details?: { fieldErrors?: Record<string, string[]> } }).details?.fieldErrors;
      if (fe) setErrors(fe);
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Settings" description="Restaurant details, hours, delivery and customer messages." actions={<Button onClick={() => void save()} disabled={busy}>{busy ? <Spinner /> : null} Save changes</Button>} />
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto grid max-w-3xl gap-6">
          <WhatsAppNumbersCard />

          <Card>
            <CardHeader><CardTitle>Restaurant</CardTitle></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" error={errors.restaurantName?.[0]}><Input value={s.restaurantName} onChange={(e) => set('restaurantName', e.target.value)} /></Field>
              <Field label="Phone"><Input value={s.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
              <div className="sm:col-span-2"><Field label="Address"><Input value={s.address} onChange={(e) => set('address', e.target.value)} /></Field></div>
              <Field label="Currency"><Input value={s.currency} maxLength={3} onChange={(e) => set('currency', e.target.value.toUpperCase())} /></Field>
              <Field label="Timezone"><Input value={s.timezone} onChange={(e) => set('timezone', e.target.value)} /></Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Receipt</CardTitle></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <p className="text-sm text-muted-foreground sm:col-span-2">Printed receipts and the PDF sent on WhatsApp show the name, address and phone above, plus these.</p>
              <Field label="Registration / VAT no. (optional)" error={errors.receiptRegistrationNo?.[0]}><Input value={s.receiptRegistrationNo} onChange={(e) => set('receiptRegistrationNo', e.target.value)} /></Field>
              <Field label="Closing line" error={errors.receiptFooter?.[0]}><Input value={s.receiptFooter} onChange={(e) => set('receiptFooter', e.target.value)} /></Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Opening hours</CardTitle>{errors.hours ? <CardDescription className="text-destructive">{errors.hours[0]}</CardDescription> : null}</CardHeader>
            <CardContent className="grid gap-2">
              {WEEKDAYS.map((d) => {
                const h = s.hours[d];
                return (
                  <div key={d} className="flex flex-wrap items-center gap-3">
                    <span className="w-24 text-sm">{DAY_LABEL[d]}</span>
                    <label className="flex items-center gap-2 text-sm"><Switch checked={!h.closed} onCheckedChange={(open) => set('hours', { ...s.hours, [d]: { ...h, closed: !open } })} /> {h.closed ? 'Closed' : 'Open'}</label>
                    {!h.closed ? (
                      <>
                        <Input type="time" className="w-28" value={h.open} onChange={(e) => set('hours', { ...s.hours, [d]: { ...h, open: e.target.value } })} />
                        <span className="text-sm text-muted-foreground">to</span>
                        <Input type="time" className="w-28" value={h.close} onChange={(e) => set('hours', { ...s.hours, [d]: { ...h, close: e.target.value } })} />
                      </>
                    ) : null}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Pickup & delivery</CardTitle></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm"><Switch checked={s.pickupEnabled} onCheckedChange={(v) => set('pickupEnabled', v)} /> Pickup</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={s.deliveryEnabled} onCheckedChange={(v) => set('deliveryEnabled', v)} /> Delivery</label>
              <Field label="Delivery fee"><Input inputMode="decimal" value={rupees(s.deliveryFeeCents)} onChange={(e) => set('deliveryFeeCents', cents(e.target.value))} /></Field>
              <Field label="Minimum order"><Input inputMode="decimal" value={rupees(s.minimumOrderCents)} onChange={(e) => set('minimumOrderCents', cents(e.target.value))} /></Field>
              <Field label="Typical prep time (minutes)"><Input type="number" min={1} max={240} value={s.prepTimeMinutes} onChange={(e) => set('prepTimeMinutes', Number(e.target.value))} /></Field>
              <div className="sm:col-span-2"><Field label="Delivery area" hint="Shown to the AI when answering delivery questions."><Textarea rows={2} value={s.deliveryAreaNotes} onChange={(e) => set('deliveryAreaNotes', e.target.value)} /></Field></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Customer messages</CardTitle>
              <CardDescription>Sent automatically on each status change while the 24-hour window is open. Placeholders: {'{name} {number} {total} {eta} {restaurant}'}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3">
              {(Object.keys(MSG_LABEL) as (keyof Settings['statusMessages'])[]).map((k) => (
                <Field key={k} label={MSG_LABEL[k]}><Textarea rows={2} value={s.statusMessages[k]} onChange={(e) => set('statusMessages', { ...s.statusMessages, [k]: e.target.value })} /></Field>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Promotions</CardTitle>
              <CardDescription>Meta starts new numbers at 250 unique customers per 24 hours and raises the limit as quality stays high. Raise this only after Meta does.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Bulk messages per 24 hours"><Input type="number" min={1} value={s.promoDailyLimit} onChange={(e) => set('promoDailyLimit', Number(e.target.value))} /></Field>
              <Field label="Send pace (messages per minute)"><Input type="number" min={1} max={600} value={s.promoSendsPerMinute} onChange={(e) => set('promoSendsPerMinute', Number(e.target.value))} /></Field>
              <Field label="Reply when a customer sends STOP"><Textarea rows={2} value={s.optOutReply} onChange={(e) => set('optOutReply', e.target.value)} /></Field>
              <Field label="Reply when a customer opts in (START / YES)"><Textarea rows={2} value={s.optInReply} onChange={(e) => set('optInReply', e.target.value)} /></Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>AI assistance</CardTitle><CardDescription>AI never confirms orders by itself. It only sends messages when auto-reply is on.</CardDescription></CardHeader>
            <CardContent className="grid gap-3">
              <label className="flex items-center gap-2 text-sm"><Switch checked={s.aiAutoExtract} onCheckedChange={(v) => set('aiAutoExtract', v)} /> Create draft orders from chats automatically</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={s.aiAutoSuggest} onCheckedChange={(v) => set('aiAutoSuggest', v)} /> Suggest replies for new messages</label>
              <Field label="Reply guidelines for the AI (tone, common questions, policies)">
                <Textarea
                  rows={4}
                  value={s.aiReplyGuidelines}
                  placeholder={'e.g. Reply in Singlish when the customer does. Call customers "sir/madam" only in English.\nParking is available behind the shop. We don\'t take card payments on delivery.'}
                  onChange={(e) => set('aiReplyGuidelines', e.target.value)}
                />
              </Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>AI auto-reply</CardTitle>
              <CardDescription>
                The AI answers simple questions (menu, prices, hours, delivery) when nobody has replied in time. It hands complaints, order
                questions and anything uncertain to staff, and never confirms orders. It sends at most 3 replies per chat per hour, and
                auto-replies are marked "AI auto-reply" in the chat.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm sm:col-span-2"><Switch checked={s.aiAutoReply} onCheckedChange={(v) => set('aiAutoReply', v)} /> Reply automatically</label>
              <Field label="Wait for staff first (seconds)">
                <Input type="number" min={0} max={3600} value={s.aiAutoReplyDelaySeconds} disabled={!s.aiAutoReply} onChange={(e) => set('aiAutoReplyDelaySeconds', Number(e.target.value))} />
              </Field>
              <Field label="Pause after a staff reply (minutes)" error={errors.aiAutoReplyStaffPauseMinutes?.[0]}>
                <Input type="number" min={0} max={240} value={s.aiAutoReplyStaffPauseMinutes} disabled={!s.aiAutoReply} onChange={(e) => set('aiAutoReplyStaffPauseMinutes', Number(e.target.value))} />
              </Field>
              <p className="text-xs text-muted-foreground sm:col-span-2">
                When a customer writes, the AI waits {s.aiAutoReplyDelaySeconds} seconds for staff to answer, then replies. If staff typed in that chat
                in the last {s.aiAutoReplyStaffPauseMinutes} minutes, it leaves the chat to them ({s.aiAutoReplyStaffPauseMinutes ? 'set 0 to never pause' : 'no pause'}). Sending a receipt doesn't count.
              </p>
              <div className="sm:col-span-2">
                <Field
                  label="When closed and the AI can't answer"
                  hint="Sent instead of silence (at most once per chat every 12 hours). {opens} becomes the next opening time, e.g. “tomorrow (Sunday) at 10:00”. Leave empty to send nothing."
                  error={errors.aiClosedNotice?.[0]}
                >
                  <Textarea rows={2} value={s.aiClosedNotice} disabled={!s.aiAutoReply} onChange={(e) => set('aiClosedNotice', e.target.value)} />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <Switch checked={s.aiAutoReplyOutsideHoursOnly} disabled={!s.aiAutoReply} onCheckedChange={(v) => set('aiAutoReplyOutsideHoursOnly', v)} /> Only outside opening hours
              </label>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
